import { AIImageBadge } from '@/components/customFields/AIImageBadge';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import DownloadIcon from '@/public/icons/download.svg';
import type { AssetStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';

import type { DownloadProps } from '../DownloadCard';
import classes from './DownloadItem.module.scss';

export const DownloadItem: React.FC<
  DownloadProps & {
    extension?: string;
    variant?: 'single' | 'multiple';
    image?: AssetStoryblok;
    imageAlt?: string;
  }
> = ({ title, file, size, extension, variant = 'single', image, imageAlt, isAIGenerated }) => {
  const fileType = isAIGenerated ? (
    <>
      (<AIImageBadge variant="inline" />
      {extension ? `, ${extension.toUpperCase()}` : null})
    </>
  ) : (
    <span className={classes['download__file-extension']} data-testid="download-item-extension">
      {extension}
    </span>
  );

  if (variant === 'single') {
    return (
      <div className={classNames(classes['download__Item'], classes['single'])} data-testid="download-item">
        <div data-testid="download-item-file-details">
          <h6
            className={classNames(classes['download__file-name'], 'font-display-7')}
            data-testid="download-item-title"
          >
            {title}
          </h6>
          <div
            className={classNames(classes['download__meta-data'], 'font-copy-small')}
            data-testid="download-item-meta"
          >
            {fileType}
            &nbsp;
            {size && (
              <span className={classes['download__file-size']} data-testid="download-item-size">
                ({size})
              </span>
            )}
          </div>
        </div>
        {image && (
          <div className={classes.imageWrapper} data-testid="download-item-image-wrapper">
            <StoryblokImage image={image} alt={imageAlt} />
          </div>
        )}
        <div className={classNames(classes['download__link'])} data-testid="download-item-action">
          <DownloadIcon aria-hidden="true" className={classes['download__icon']} />
          <InlineTextLink
            className={classes['download__anchor']}
            href={file}
            target="_blank"
            data-testid="download-card-link"
            suppressArrowIcon
          >
            Herunterladen
          </InlineTextLink>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(classes['download__Item'], classes['multiple'])} data-testid="download-item">
      <DownloadIcon aria-hidden="true" className={classes['download__icon']} />
      <div className={classes.downloadBox} data-testid="download-item-file-details">
        <InlineTextLink
          className={classNames(classes['download__link'], classes['download__anchor'])}
          href={file}
          target="_blank"
          data-testid="download-card-link"
          suppressArrowIcon
        >
          {title}
        </InlineTextLink>
        <div className={classNames(classes['download__meta-data'], 'font-copy-small')} data-testid="download-item-meta">
          {fileType}
          &nbsp;
          {size && (
            <span className={classes['download__file-size']} data-testid="download-item-size">
              ({size})
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadItem;
